<template>
  <div class="prepeers-phone">
    <label
      v-if="props.label"
      class="prepeers-phone__label">
      {{ props.label }}
    </label>

    <div class="prepeers-phone__row">
      <div
        ref="prefixRootRef"
        class="prepeers-phone__prefix-wrap"
        :class="{
          'prepeers-phone__prefix-wrap--disabled': props.disabled,
          'prepeers-phone__prefix-wrap--open': isPrefixOpen,
        }">
        <button
          type="button"
          class="prepeers-phone__prefix-trigger"
          :disabled="props.disabled"
          :aria-expanded="isPrefixOpen"
          aria-haspopup="listbox"
          @click="togglePrefixList">
          <span
            class="prepeers-phone__prefix-flag fi"
            :class="`fi-${selectedCountry.flagCode}`"
            aria-hidden="true" />
          <span class="prepeers-phone__prefix-value">{{ selectedCountry.prefix }}</span>
          <span
            class="prepeers-phone__prefix-chevron"
            aria-hidden="true" />
        </button>

        <Teleport to="body">
          <ul
            v-show="isPrefixOpen"
            ref="prefixListRef"
            class="prepeers-phone__prefix-list"
            role="listbox"
            :style="prefixListStyle"
            :aria-activedescendant="`phone-prefix-${selectedCountry.code}`">
            <li
              v-for="country in COUNTRY_PREFIXES"
              :id="`phone-prefix-${country.code}`"
              :key="country.code"
              role="option"
              :aria-selected="country.prefix === props.prefixValue">
              <button
                type="button"
                class="prepeers-phone__prefix-option"
                :class="{
                  'prepeers-phone__prefix-option--selected': country.prefix === props.prefixValue,
                }"
                @click="selectPrefix(country.prefix)">
                <span
                  class="prepeers-phone__prefix-option-flag fi"
                  :class="`fi-${country.flagCode}`"
                  aria-hidden="true" />
                <span class="prepeers-phone__prefix-option-label">{{ country.prefix }}</span>
              </button>
            </li>
          </ul>
        </Teleport>
      </div>

      <input
        class="prepeers-phone__number"
        :class="{ 'prepeers-phone__number--error': !!props.error }"
        type="tel"
        :value="props.numberValue"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :required="props.required"
        :autocomplete="props.autocomplete"
        :name="props.name"
        @input="onNumberInput" />
    </div>

    <p
      v-if="props.error"
      class="prepeers-phone__error">
      {{ props.error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import type { ComputedRef, CSSProperties, PropType, Ref } from 'vue'

interface CountryPrefix {
  code: string
  flagCode: string
  prefix: string
}

const COUNTRY_PREFIXES: CountryPrefix[] = [
  { code: 'FR', flagCode: 'fr', prefix: '+33' },
  { code: 'BE', flagCode: 'be', prefix: '+32' },
  { code: 'CH', flagCode: 'ch', prefix: '+41' },
  { code: 'LU', flagCode: 'lu', prefix: '+352' },
  { code: 'US', flagCode: 'us', prefix: '+1' },
]

const DEFAULT_COUNTRY: CountryPrefix = COUNTRY_PREFIXES[0]!

const PREFIX_LIST_GAP_PX = 4
const VIEWPORT_PADDING_PX = 8
const ESTIMATED_PREFIX_ITEM_HEIGHT_PX = 36

const props = defineProps({
  prefixValue: {
    type: String,
    required: true,
  },
  numberValue: {
    type: String,
    required: true,
  },
  label: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  placeholder: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  error: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  name: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
})

const emit = defineEmits<{
  'update:prefixValue': [value: string]
  'update:numberValue': [value: string]
}>()

const prefixRootRef: Ref<HTMLElement | null> = ref(null)
const prefixListRef: Ref<HTMLElement | null> = ref(null)
const isPrefixOpen: Ref<boolean> = ref(false)
const prefixListStyle: Ref<CSSProperties> = ref({})

const selectedCountry: ComputedRef<CountryPrefix> = computed((): CountryPrefix => {
  return (
    COUNTRY_PREFIXES.find((entry: CountryPrefix): boolean => entry.prefix === props.prefixValue) ??
    DEFAULT_COUNTRY
  )
})

/**
 * Applique le positionnement fixed du menu (bas par défaut, haut si manque de place).
 * @param {HTMLElement} root - Conteneur du sélecteur d'indicatif.
 * @param {HTMLElement | null} list - Liste téléportée des préfixes.
 * @returns {void}
 */
function applyPrefixListPosition(root: HTMLElement, list: HTMLElement | null): void {
  const rect: DOMRect = root.getBoundingClientRect()
  const measuredHeight: number = list?.getBoundingClientRect().height ?? 0
  const listHeight: number =
    measuredHeight > 0 ? measuredHeight : COUNTRY_PREFIXES.length * ESTIMATED_PREFIX_ITEM_HEIGHT_PX
  const viewportHeight: number = window.innerHeight
  const spaceBelow: number = viewportHeight - rect.bottom - VIEWPORT_PADDING_PX
  const spaceAbove: number = rect.top - VIEWPORT_PADDING_PX
  const openUpward: boolean = spaceBelow < listHeight && spaceAbove >= spaceBelow

  const baseStyle: CSSProperties = {
    position: 'fixed',
    left: `${rect.left}px`,
    minWidth: `${rect.width}px`,
  }

  prefixListStyle.value = openUpward
    ? {
        ...baseStyle,
        top: 'auto',
        bottom: `${viewportHeight - rect.top + PREFIX_LIST_GAP_PX}px`,
      }
    : {
        ...baseStyle,
        top: `${rect.bottom + PREFIX_LIST_GAP_PX}px`,
        bottom: 'auto',
      }
}

/**
 * Positionne la liste téléportée sous le bouton indicatif, ou au-dessus si manque de place.
 * @returns {Promise<void>}
 */
async function updatePrefixListPosition(): Promise<void> {
  const root: HTMLElement | null = prefixRootRef.value
  if (!root) return

  await nextTick()
  applyPrefixListPosition(root, prefixListRef.value)

  requestAnimationFrame((): void => {
    if (!isPrefixOpen.value || !prefixRootRef.value) return
    applyPrefixListPosition(prefixRootRef.value, prefixListRef.value)
  })
}

/**
 * Recalcule la position du menu lors d'un scroll ou resize.
 * @returns {void}
 */
function onViewportChange(): void {
  const root: HTMLElement | null = prefixRootRef.value
  if (!root) return
  applyPrefixListPosition(root, prefixListRef.value)
}

/**
 * Ouvre ou ferme la liste des préfixes de pays.
 * @returns {void}
 */
function togglePrefixList(): void {
  if (props.disabled) return
  isPrefixOpen.value = !isPrefixOpen.value
}

/**
 * Ferme la liste des préfixes de pays.
 * @returns {void}
 */
function closePrefixList(): void {
  isPrefixOpen.value = false
}

/**
 * Sélectionne un préfixe de pays.
 * @param {string} prefix - Indicatif sélectionné (ex. "+33").
 * @returns {void}
 */
function selectPrefix(prefix: string): void {
  emit('update:prefixValue', prefix)
  closePrefixList()
}

/**
 * Propage la valeur saisie au parent via v-model.
 * @param {Event} event - Événement DOM natif de l'input.
 * @returns {void}
 */
function onNumberInput(event: Event): void {
  emit('update:numberValue', (event.target as HTMLInputElement).value)
}

/**
 * Ferme la liste des préfixes de pays lorsque l'utilisateur clique en dehors de la liste.
 * @param {PointerEvent} event - Événement DOM natif de la souris.
 * @returns {void}
 */
function onDocumentPointerDown(event: PointerEvent): void {
  if (!isPrefixOpen.value || !(event.target instanceof Node)) return

  const root: HTMLElement | null = prefixRootRef.value
  const list: HTMLElement | null = prefixListRef.value
  if (root?.contains(event.target) || list?.contains(event.target)) return

  closePrefixList()
}

/**
 * Ferme la liste des préfixes de pays lorsque l'utilisateur appuie sur la touche Escape.
 * @param {KeyboardEvent} event - Événement DOM natif du clavier.
 * @returns {void}
 */
function onDocumentKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape') closePrefixList()
}

watch(isPrefixOpen, (open: boolean): void => {
  if (!import.meta.client) return

  if (open) {
    void updatePrefixListPosition()
    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)
    return
  }

  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
})

onMounted((): void => {
  document.addEventListener('pointerdown', onDocumentPointerDown)
  document.addEventListener('keydown', onDocumentKeyDown)
})

onUnmounted((): void => {
  document.removeEventListener('pointerdown', onDocumentPointerDown)
  document.removeEventListener('keydown', onDocumentKeyDown)
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
})
</script>

<style scoped>
.prepeers-phone {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.prepeers-phone__label {
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
}

.prepeers-phone__row {
  display: flex;
  gap: var(--space-2);
}

.prepeers-phone__prefix-wrap {
  position: relative;
  display: flex;
  flex-shrink: 0;
}

.prepeers-phone__prefix-wrap--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.prepeers-phone__prefix-trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 5.5rem;
  padding: 0.7em 0.8em;
  background: var(--bg-elevated);
  border: 1.5px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-family: var(--font-body);
  cursor: pointer;
  outline: none;
  transition:
    border-color var(--duration-base) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out);
}

.prepeers-phone__prefix-wrap--open .prepeers-phone__prefix-trigger,
.prepeers-phone__prefix-trigger:focus-visible {
  border-color: var(--accent-blue);
  box-shadow: 0 0 0 3px rgba(3, 160, 241, 0.2);
}

.prepeers-phone__prefix-flag {
  flex-shrink: 0;
  width: 1.25em;
  line-height: 1;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.prepeers-phone__prefix-value {
  flex: 1;
  text-align: left;
}

.prepeers-phone__prefix-chevron {
  flex-shrink: 0;
  width: 0.45em;
  height: 0.45em;
  margin-left: 0.15em;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg) translateY(-1px);
  opacity: 0.75;
}

.prepeers-phone__prefix-list {
  z-index: 10000;
  margin: 0;
  padding: var(--space-1);
  list-style: none;
  background: var(--bg-elevated);
  border: 1.5px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}

.prepeers-phone__prefix-option {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: 0.55em 0.65em;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-family: var(--font-body);
  text-align: left;
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out);
}

.prepeers-phone__prefix-option:hover,
.prepeers-phone__prefix-option--selected {
  background: rgba(3, 160, 241, 0.15);
}

.prepeers-phone__prefix-option-flag {
  flex-shrink: 0;
  width: 1.25em;
  line-height: 1;
  border-radius: 2px;
}

.prepeers-phone__prefix-option-label {
  flex: 1;
}

.prepeers-phone__number {
  flex: 1;
  min-width: 0;
  padding: 0.7em 1.1em;
  background: var(--bg-elevated);
  border: 1.5px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-family: var(--font-body);
  outline: none;
  transition:
    border-color var(--duration-base) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out);
}

.prepeers-phone__number::placeholder {
  color: var(--text-muted);
}

.prepeers-phone__number:focus {
  border-color: var(--accent-blue);
  box-shadow: 0 0 0 3px rgba(3, 160, 241, 0.2);
}

.prepeers-phone__number--error {
  border-color: var(--color-error);
}

.prepeers-phone__number:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prepeers-phone__error {
  font-size: var(--text-xs);
  color: var(--color-error);
  line-height: var(--leading-snug);
}

/* iOS : évite le zoom au focus si font-size < 16px */
@media (max-width: 1024px) {
  .prepeers-phone__number {
    font-size: 16px;
  }
}
</style>
